import classNames from 'classnames'

/**
 * Loading spinner component with different sizes and variants
 * 
 * @param {Object} props - Component props
 * @param {string} size - Loader size (sm, md, lg, xl)
 * @param {string} variant - Loader variant (primary, secondary, white)
 * @param {string} text - Optional loading text
 * @param {boolean} fullScreen - Show as full screen overlay
 * @param {string} className - Additional CSS classes
 */
const Loader = ({ 
  size = 'md', 
  variant = 'primary', 
  text = '', 
  fullScreen = false,
  className 
}) => {
  const sizes = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4',
    xl: 'w-16 h-16 border-4',
  }

  const variants = {
    primary: 'border-primary-500 border-t-transparent',
    secondary: 'border-secondary-500 border-t-transparent',
    white: 'border-white border-t-transparent',
  }

  const spinnerClasses = classNames(
    'animate-spin rounded-full',
    sizes[size],
    variants[variant],
    className
  )

  const content = (
    <div className="flex flex-col items-center justify-center gap-3">
      <div className={spinnerClasses} role="status" aria-label="Chargement">
        <span className="sr-only">Chargement...</span>
      </div>
      {text && (
        <p className="text-sm text-gray-600 font-medium">{text}</p>
      )}
    </div>
  )

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90 backdrop-blur-sm">
        {content}
      </div>
    )
  }

  return content
}

/**
 * Loading skeleton component for content placeholders
 */
export const Skeleton = ({ className, width, height }) => {
  return (
    <div
      className={classNames('animate-pulse bg-gray-200 rounded', className)}
      style={{ width, height }}
    />
  )
}

/**
 * Loading skeleton for text lines
 */
export const SkeletonText = ({ lines = 3, className }) => {
  return (
    <div className={classNames('space-y-3', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-4"
          width={i === lines - 1 ? '70%' : '100%'}
        />
      ))}
    </div>
  )
}

/**
 * Loading skeleton for cards
 */
export const SkeletonCard = ({ className }) => {
  return (
    <div className={classNames('card p-6 space-y-4', className)}>
      <div className="flex items-center gap-4">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <SkeletonText lines={3} />
    </div>
  )
}

/**
 * Loading skeleton for tables
 */
export const SkeletonTable = ({ rows = 5, cols = 4 }) => {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-8" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: cols }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-10" />
          ))}
        </div>
      ))}
    </div>
  )
}

export default Loader


